/**
 * Inline script run before React hydrates, on every page (root layout).
 *
 * Browser auto-translation (Chrome / Google Translate) rewrites the page by
 * wrapping each text node in <font> elements. React still holds the original
 * text node, so when it later removes or reorders one it calls
 * `parent.removeChild(node)` / `parent.insertBefore(node, ref)` on a node that
 * is no longer that parent's child, the browser throws NotFoundError, and the
 * error boundary replaces the whole page with "Something went wrong"
 * (facebook/react#11538). On /apply that took down the booking calendar for a
 * visitor whose Chrome translated the English page into Italian.
 *
 * The patch makes the two calls tolerant: removing a node that is not our child
 * is a warned no-op (the node is already detached from where React looked), and
 * inserting before a reference that is not our child appends instead of
 * throwing. Translation itself stays available to visitors who need it.
 * Idempotent: a marker on the prototype keeps a second run from wrapping again.
 *
 * It also latches `window[PAGE_TRANSLATED_FLAG]` the moment the browser marks
 * <html> as translated (Chrome's translated-ltr or translated-rtl class). A page
 * translated before React hydrates no longer matches the server HTML, React
 * reports the mismatch (minified error 418) and re-renders the document, which
 * resets <html> and drops the <font> wrappers, so by the time the error is
 * captured only this latch still records that the browser translated the page.
 * web/lib/observability/posthog-scrub.ts reads it.
 *
 * The Dark Reader extension gets the same treatment under
 * `window[DARK_READER_FLAG]`. It inserts <style class="darkreader"> siblings
 * next to our inline <style> blocks inside <body> before React hydrates, which
 * is an element mismatch (minified error 418, argument HTML) that React
 * reports and recovers from. By the time the error is captured React's
 * re-render has removed its attributes from <html> and the style tags from
 * <body>, so only the latch still records it.
 */
export const PAGE_TRANSLATED_FLAG = "__caPageTranslated"
export const DARK_READER_FLAG = "__caDarkReader"

export const TRANSLATE_DOM_GUARD_SCRIPT = `(function(){
if(typeof Node!=="function"||!Node.prototype||Node.prototype.__caTranslateGuard)return;
Object.defineProperty(Node.prototype,"__caTranslateGuard",{value:true});
var warn=function(m,a,b){if(typeof console!=="undefined"&&console.warn)console.warn(m,a,b)};
var removeChild=Node.prototype.removeChild;
Node.prototype.removeChild=function(child){
if(child&&child.parentNode!==this){warn("Skipped removeChild: node is not a child of this parent (page translation).",child,this);return child}
return removeChild.apply(this,arguments)};
var insertBefore=Node.prototype.insertBefore;
Node.prototype.insertBefore=function(node,ref){
if(ref&&ref.parentNode!==this){warn("insertBefore reference is not a child of this parent (page translation); appending.",ref,this);return insertBefore.call(this,node,null)}
return insertBefore.apply(this,arguments)};
var html=document.documentElement;
if(html&&typeof MutationObserver==="function"){
var latch=function(){if(/\\btranslated-(?:ltr|rtl)\\b/.test(html.className)){window["${PAGE_TRANSLATED_FLAG}"]=true;return true}return false};
if(!latch()){var observer=new MutationObserver(function(){if(latch())observer.disconnect()});observer.observe(html,{attributes:true,attributeFilter:["class"]})}
var darkLatch=function(){if(html.hasAttribute("data-darkreader-mode")||html.hasAttribute("data-darkreader-scheme")||document.querySelector("style.darkreader,meta[name=darkreader]")){window["${DARK_READER_FLAG}"]=true;return true}return false};
if(!darkLatch()){var darkObserver=new MutationObserver(function(){if(darkLatch())darkObserver.disconnect()});darkObserver.observe(html,{attributes:true,attributeFilter:["data-darkreader-mode","data-darkreader-scheme"]})}}
})();`
